import { Component, HostListener, Output, EventEmitter, Input, OnChanges, SimpleChanges, ElementRef, ChangeDetectorRef } from '@angular/core';

@Component({
  standalone: false,
  selector: 'app-sqft-filters',
  templateUrl: './sqft-filters.component.html',
  styleUrls: ['./sqft-filters.component.scss']
})
export class SqftFiltersComponent implements OnChanges {
  @Input() selectedRange: {minValue: number, maxValue: number} | null = null;
  @Input() externalRangeMin: number | null = null;
  @Input() externalRangeMax: number | null = null;
  @Output() selectionChange = new EventEmitter<{minValue: number, maxValue: number} | null>();

  // SQFT Range specific properties
  rangeMin = 1000;
  rangeMax = 200000;
  minValue = 1000;
  maxValue = 200000;
  minInputValue = 1000;
  maxInputValue = 200000;
  hasMinError = false;
  hasMaxError = false;

  // Initial values for comparison
  initialMinValue = 1000;
  initialMaxValue = 200000;

  // Drag state
  private isDragging = false;
  private dragHandle: 'min' | 'max' | null = null;
  private sliderRect: DOMRect | null = null;

  selected: boolean = false;

  constructor(private el: ElementRef, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['externalRangeMin'] || changes['externalRangeMax']) {
      if (this.externalRangeMin !== null && this.externalRangeMax !== null) {
        this.rangeMin = this.externalRangeMin;
        this.rangeMax = this.externalRangeMax;
        this.initialMinValue = this.externalRangeMin;
        this.initialMaxValue = this.externalRangeMax;
        if (!this.selectedRange) {
          this.minValue = this.externalRangeMin;
          this.maxValue = this.externalRangeMax;
          this.minInputValue = this.externalRangeMin;
          this.maxInputValue = this.externalRangeMax;
        }
      }
    }
    this.updateSelections();
  }

  private updateSelections(): void {
    if (this.selectedRange) {
      this.minValue = this.selectedRange.minValue;
      this.maxValue = this.selectedRange.maxValue;
      this.minInputValue = this.selectedRange.minValue;
      this.maxInputValue = this.selectedRange.maxValue;
    } else if (!this.externalRangeMin && !this.externalRangeMax) {
      // Reset to initial values when no selection and no external range
      this.minValue = this.initialMinValue;
      this.maxValue = this.initialMaxValue;
      this.minInputValue = this.initialMinValue;
      this.maxInputValue = this.initialMaxValue;
    }
    this.hasMinError = false;
    this.hasMaxError = false;
  }

  // Range slider methods
  getMinHandlePosition(): number {
    return ((this.minValue - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
  }

  getMaxHandlePosition(): number {
    return ((this.maxValue - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
  }

  getProgressLeft(): number {
    return this.getMinHandlePosition();
  }

  getProgressWidth(): number {
    return this.getMaxHandlePosition() - this.getMinHandlePosition();
  }

  startDrag(handle: 'min' | 'max', event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.dragHandle = handle;
    // Capture rect once on mousedown — avoids stale/null lookups on every mousemove
    const slider = this.el.nativeElement.querySelector('.slider-wrapper') as HTMLElement;
    this.sliderRect = slider ? slider.getBoundingClientRect() : null;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging || !this.dragHandle || !this.sliderRect) return;
    if (this.sliderRect.width === 0) return;

    const percentage = Math.max(0, Math.min(100, ((event.clientX - this.sliderRect.left) / this.sliderRect.width) * 100));
    const value = Math.round(this.rangeMin + (percentage / 100) * (this.rangeMax - this.rangeMin));

    if (this.dragHandle === 'min') {
      this.minValue = Math.min(value, this.maxValue);
      this.minInputValue = this.minValue;
    } else {
      this.maxValue = Math.max(value, this.minValue);
      this.maxInputValue = this.maxValue;
    }

    this.validateInputs();
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.dragHandle = null;
    this.sliderRect = null;
    this.emitSelectionChange();
  }

  // Input handling
  onMinInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const filteredValue = this.filterNumericInput(target.value);
    target.value = filteredValue;
    const value = this.parseInputValue(filteredValue);
    if (!isNaN(value)) {
      this.minInputValue = value;
      this.minValue = value;
      this.validateInputs();
      this.emitSelectionChange();
    }
  }

  onMaxInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const filteredValue = this.filterNumericInput(target.value);
    target.value = filteredValue;
    const value = this.parseInputValue(filteredValue);
    if (!isNaN(value)) {
      this.maxInputValue = value;
      this.maxValue = value;
      this.validateInputs();
      this.emitSelectionChange();
    }
  }

  private filterNumericInput(value: string): string {
    return value.replace(/[^0-9,.]/g, '');
  }

  private emitSelectionChange(): void {
    // Emit if values have changed from initial values
    const hasChanged = this.minInputValue !== this.initialMinValue || this.maxInputValue !== this.initialMaxValue;
    this.selectionChange.emit(hasChanged ? {
      minValue: this.minInputValue,
      maxValue: this.maxInputValue
    } : null);
  }

  resetSelections(): void {
    this.minValue = this.initialMinValue;
    this.maxValue = this.initialMaxValue;
    this.minInputValue = this.initialMinValue;
    this.maxInputValue = this.initialMaxValue;
    this.hasMinError = false;
    this.hasMaxError = false;
    this.selected = false;
    this.emitSelectionChange();
  }

  validateInputs(): void {
    this.hasMinError = this.minInputValue < this.rangeMin || this.minInputValue > this.rangeMax;
    this.hasMaxError = this.maxInputValue < this.rangeMin || this.maxInputValue > this.rangeMax || this.maxInputValue < this.minInputValue;
  }

  // Formatting utilities
  formatNumber(value: number): string {
    if (value % 1 !== 0) {
      return value.toFixed(2);
    }
    if (value >= 1000) {
      return Math.floor(value).toLocaleString();
    }
    return value.toString();
  }

  formatInputValue(value: number): string {
    if (value % 1 !== 0) {
      return value.toFixed(2);
    }
    return Math.floor(value).toLocaleString();
  }

  parseInputValue(value: string): number {
    return parseFloat(value.replace(/,/g, ''));
  }

  updateRangeAndPositions(newRangeMin: number, newRangeMax: number, newMinValue: number, newMaxValue: number): void {
    this.rangeMin = newRangeMin;
    this.rangeMax = newRangeMax;
    this.minValue = newMinValue;
    this.maxValue = newMaxValue;
    this.minInputValue = newMinValue;
    this.maxInputValue = newMaxValue;
    this.initialMinValue = newRangeMin;
    this.initialMaxValue = newRangeMax;
    // Reset selection state when range changes
    this.selected = false;
    this.cdr.detectChanges();
  }

  toggleSelection(): void {
    this.selected = !this.selected;
  }
}